import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { AnomalyEventList } from "../components/AnomalyEventList"
import { getCardStyle } from "../components/deepDiveCardStyles"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

interface SyllableRateResultJson {
  syllables_per_second?: number
  inter_syllable_cv?: number
  npvi?: number
  rhythm_irregularity_score?: number
}

const CATEGORY = "syllable_rate"
const SEVERITY_THRESHOLD = 0.5

export class SyllableRateMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "medium" as const
  unit = "/s"
  aggregate_units = "/s"

  renderMetricType(): string {
    return "Syllable Rate"
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const resultJson = (mo.result as Record<string, unknown> | undefined)
      ?.raw_values as SyllableRateResultJson | undefined
    const subvalues = mo.subvalues_by_timestamp ?? []
    const syllableRate = mo.float_value ?? resultJson?.syllables_per_second ?? 0
    const cv = resultJson?.inter_syllable_cv
    const npvi = resultJson?.npvi
    const irregularity = resultJson?.rhythm_irregularity_score
    const card = getCardStyle(irregularity ?? 0, 0.3)

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className={`rounded-lg p-3 ${card.bg}`}>
            <div className={card.label}>Syllable Rate</div>
            <div className={card.value}>{syllableRate.toFixed(1)} syl/s</div>
          </div>
          {cv != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>CV</div>
              <div className={card.value}>{cv.toFixed(2)}</div>
            </div>
          )}
          {npvi != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>nPVI</div>
              <div className={card.value}>{npvi.toFixed(1)}</div>
            </div>
          )}
          {irregularity != null && (
            <div className={`rounded-lg p-3 ${card.bg}`}>
              <div className={card.label}>Irregularity</div>
              <div className={card.value}>{irregularity.toFixed(2)}</div>
            </div>
          )}
        </div>

        <AnomalyEventList
          subvalues={subvalues}
          severityThreshold={SEVERITY_THRESHOLD}
          chipLabel="Rate"
          title="Rate Anomalies"
        />

        {subvalues.length === 0 && (
          <div className="text-sm text-coval-text-muted">
            No syllable rate anomalies detected
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(mo: MetricOutputDisplaySource): AudioRegionParams[] {
    if (mo?.error_message) {
      return []
    }
    try {
      const subvalues = mo.subvalues_by_timestamp ?? []
      return subvalues
        .filter((item) => (item.float_value ?? 0) >= SEVERITY_THRESHOLD)
        .map((item, idx) => {
          const severity = item.float_value ?? 0
          const alpha = Math.min(0.2 + severity * 0.5, 0.7)
          return {
            id: `${CATEGORY}:${idx}`,
            start: item.start_offset,
            end: Math.max(item.end_offset, item.start_offset + 0.5),
            color: `rgba(59, 130, 246, ${alpha.toFixed(2)})`,
            drag: false,
            resize: false,
          }
        })
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "raw_count",
      buildReviewedResultJson: ({ durationSeconds }) => {
        return {
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        }
      },
    }
  }
}
